import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects';
import qs from 'qs';
import NotificationActionType from 'actions/notification/notificationActionType';
import {
  changeLoadingState, setSuccessMessage, setErrorMessage, changeEndedStatus, changeGenerateReportEndedStatus,
} from 'app/settings/slices';
import {
  changeNotificationSentStatus,
  saveMassMessagesState,
  saveMessagesTotal,
  getMassMessageSuccess,
  saveMassMessageRecipients,
  saveTitleExists,
  saveBonusNotification,
  saveBonusNotifications,
} from 'actions/notification/notificationActionCreator';
import NetworkService from 'services/networkService';
import { selectMassMessages, selectMessagesTotal } from 'selectors';
import { i18n, DateService } from 'services';
import { AppConstants } from 'constants/index';
import { getError, getMessage } from 'utils/helpers';
import { isJSON } from 'utils/utils';

const { getFormattedDate } = DateService;

function* getMassMessage({ payload }) {
  try {
    const { NotificationAdmin, MassMessages } = AppConstants.api;
    yield put(changeLoadingState(true));
    const response = yield call(NetworkService.makeAPIGetRequest, [NotificationAdmin, MassMessages, payload.id]);
    yield put(getMassMessageSuccess(response.data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err, true);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* saveMassMessages({ payload }) {
  try {
    const { isDraft, scheduleTime } = payload.data;
    const { NotificationAdmin, MassMessages } = AppConstants.api;
    yield put(changeLoadingState(true));
    yield call(NetworkService.makeAPIPostRequest, [NotificationAdmin, MassMessages], payload);
    let message = i18n.t('notification:slSent');
    if (isDraft) {
      message = i18n.t('notification:slSavedAsDraft');
    } else if (scheduleTime) {
      message = i18n.t('notification:slScheduledForDate', { date: getFormattedDate(scheduleTime, AppConstants.format.dateTime) });
    }
    yield put(setSuccessMessage(getMessage(i18n.t('communication:message'), message)));
    yield put(changeLoadingState(false));
    yield put(changeNotificationSentStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* updateMassMessage({ payload }) {
  try {
    const { isDraft, scheduleTime } = payload.data;
    const { NotificationAdmin, MassMessages } = AppConstants.api;
    yield put(changeLoadingState(true));
    yield call(NetworkService.makeAPIPutRequest, [NotificationAdmin, MassMessages], payload);
    yield put(changeEndedStatus(true));
    let message = i18n.t('notification:slSent');
    if (isDraft) {
      message = i18n.t('notification:slSavedAsDraft');
    } else if (scheduleTime) {
      message = i18n.t('notification:slScheduledForDate', { date: getFormattedDate(scheduleTime, AppConstants.format.dateTime) });
    }
    yield put(setSuccessMessage(getMessage(i18n.t('communication:message'), message)));
    yield put(changeLoadingState(false));
    yield put(changeNotificationSentStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* moveToDraft({ payload }) {
  try {
    const { NotificationAdmin, MassMessages, MoveToDraft } = AppConstants.api;
    yield put(changeLoadingState(true));
    yield call(NetworkService.makeAPIPutRequest, [NotificationAdmin, MassMessages, MoveToDraft, payload.id]);
    yield put(changeEndedStatus(true));
    yield put(setSuccessMessage(getMessage(i18n.t('communication:message'), i18n.t('notification:movedToDraft'))));
    yield put(changeLoadingState(false));
    yield put(changeNotificationSentStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getSentMassMessages({ payload }) {
  try {
    const options = {
      params: payload.data,
      paramsSerializer: (params) => qs.stringify(params),
    };
    yield put(changeLoadingState(true));
    const { NotificationAdmin, MassMessages, Sent } = AppConstants.api;
    const response = yield call(NetworkService.makeAPIGetRequest, [NotificationAdmin, MassMessages, Sent], options);
    const { massMessages, total } = response.data;
    const state = yield select();
    const massMessagesState = selectMassMessages(state.notification);
    const totalState = selectMessagesTotal(state.notification);
    yield put(
      saveMassMessagesState({
        ...massMessagesState,
        sent: massMessages,
      }),
    );
    yield put(
      saveMessagesTotal({
        ...totalState,
        sent: total,
      }),
    );
    yield put(changeLoadingState(false));
  } catch (err) {
    yield put(changeLoadingState(false));
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
  }
}

function* getScheduledMassMessages({ payload }) {
  try {
    const options = {
      params: payload.data,
      paramsSerializer: (params) => qs.stringify(params),
    };
    yield put(changeLoadingState(true));
    const { NotificationAdmin, MassMessages, Scheduled } = AppConstants.api;
    const response = yield call(NetworkService.makeAPIGetRequest, [NotificationAdmin, MassMessages, Scheduled], options);
    const { massMessages, total } = response.data;
    const state = yield select();
    const massMessagesState = selectMassMessages(state.notification);
    const totalState = selectMessagesTotal(state.notification);
    yield put(
      saveMassMessagesState({
        ...massMessagesState,
        scheduled: massMessages,
      }),
    );
    yield put(
      saveMessagesTotal({
        ...totalState,
        scheduled: total,
      }),
    );
    yield put(changeLoadingState(false));
  } catch (err) {
    yield put(changeLoadingState(false));
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
  }
}

function* getDraftMassMessage({ payload }) {
  try {
    const options = {
      params: payload.data,
      paramsSerializer: (params) => qs.stringify(params),
    };
    yield put(changeLoadingState(true));
    const { NotificationAdmin, MassMessages, Draft } = AppConstants.api;
    const response = yield call(NetworkService.makeAPIGetRequest, [NotificationAdmin, MassMessages, Draft], options);
    const { massMessages, total } = response.data;
    const state = yield select();
    const massMessagesState = selectMassMessages(state.notification);
    const totalState = selectMessagesTotal(state.notification);
    yield put(
      saveMassMessagesState({
        ...massMessagesState,
        draft: massMessages,
      }),
    );
    yield put(
      saveMessagesTotal({
        ...totalState,
        draft: total,
      }),
    );
    yield put(changeLoadingState(false));
  } catch (err) {
    yield put(changeLoadingState(false));
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
  }
}

function* deleteMassMessage({ payload }) {
  try {
    const { id, name } = payload;
    yield put(changeLoadingState(true));
    const { NotificationAdmin, MassMessages } = AppConstants.api;
    yield call(NetworkService.makeAPIDeleteRequest, [NotificationAdmin, MassMessages, id]);
    yield put(changeEndedStatus(true));
    yield put(setSuccessMessage(getMessage(i18n.t('communication:lMessage'), i18n.t('notification:slDeleted'), name)));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getMassMessageRecipients({ payload }) {
  try {
    const options = {
      params: payload.data,
      paramsSerializer: (params) => qs.stringify(params),
    };
    yield put(changeLoadingState(true));
    const { NotificationAdmin, MassMessages, RecipientsDetail } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [NotificationAdmin, MassMessages, RecipientsDetail], options);
    yield put(saveMassMessageRecipients(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    yield put(changeLoadingState(false));
    if (err && !isJSON(err.message)) {
      const error = getError(err, true);
      yield put(setErrorMessage(error));
    }
  }
}

function* generateSentMassMessages({ payload }) {
  try {
    const options = {
      params: payload.data,
      paramsSerializer: (param) => qs.stringify(param, { allowDots: true }),
    };
    yield put(changeLoadingState(true));
    const {
      NotificationAdmin, MassMessages, Sent, Generate,
    } = AppConstants.api;
    yield call(NetworkService.makeAPIGetRequest, [NotificationAdmin, MassMessages, Sent, Generate], options);
    yield put(changeLoadingState(false));
    yield put(changeGenerateReportEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* generateMassMessageRecipients({ payload }) {
  try {
    const options = {
      params: payload.data,
      paramsSerializer: (param) => qs.stringify(param, { allowDots: true }),
    };
    yield put(changeLoadingState(true));
    const {
      NotificationAdmin, MassMessages, RecipientsDetail, Generate,
    } = AppConstants.api;
    yield call(NetworkService.makeAPIGetRequest, [NotificationAdmin, MassMessages, RecipientsDetail, Generate], options);
    yield put(changeLoadingState(false));
    yield put(changeGenerateReportEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* checkTitleExists({ payload }) {
  try {
    const { brandId, params, id } = payload;
    const { NotificationAdmin, MassMessages, TitleExists } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [NotificationAdmin, MassMessages, TitleExists, brandId, id], { params });
    yield put(saveTitleExists(data));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
  }
}

function* getBonusNotifications({ payload }) {
  try {
    const { params, id } = payload;
    const options = {
      params,
    };
    yield put(changeLoadingState(true));
    const { NotificationAdmin, BonusNotifications, ByPlayer } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [NotificationAdmin, BonusNotifications, ByPlayer, id], options);
    yield put(saveBonusNotifications(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getBonusNotification({ payload }) {
  try {
    const { messageId, playerId } = payload;
    const options = {
      params: { messageId },
    };
    yield put(changeLoadingState(true));
    const { NotificationAdmin, BonusNotifications, MessageByPlayer } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [NotificationAdmin, BonusNotifications, MessageByPlayer, playerId], options);
    yield put(saveBonusNotification(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

export default function* notificationSaga() {
  yield all([
    takeLatest(NotificationActionType.GET_MASS_MESSAGE, getMassMessage),
    takeLatest(NotificationActionType.UPDATE_MASS_MESSAGE, updateMassMessage),
    takeLatest(NotificationActionType.MOVE_TO_DRAFT, moveToDraft),
    takeLatest(NotificationActionType.SAVE_MASS_MESSAGES, saveMassMessages),
    takeLatest(NotificationActionType.GET_SENT_MASS_MESSAGES, getSentMassMessages),
    takeLatest(NotificationActionType.GET_SCHEDULED_MASS_MESSAGES, getScheduledMassMessages),
    takeLatest(NotificationActionType.GET_DRAFT_MASS_MESSAGES, getDraftMassMessage),
    takeLatest(NotificationActionType.DELETE_MASS_MESSAGE, deleteMassMessage),
    takeLatest(NotificationActionType.GET_MASS_MESSAGE_RECIPIENTS, getMassMessageRecipients),
    takeLatest(NotificationActionType.GENERATE_SENT_MASS_MESSAGES, generateSentMassMessages),
    takeLatest(NotificationActionType.GENERATE_MASS_MESSAGE_RECIPIENTS, generateMassMessageRecipients),
    takeLatest(NotificationActionType.CHECK_TITLE_EXISTS, checkTitleExists),
    takeLatest(NotificationActionType.GET_BONUS_NOTIFICATIONS, getBonusNotifications),
    takeLatest(NotificationActionType.GET_BONUS_NOTIFICATION, getBonusNotification),
  ]);
}
